import request from '@oskecare/fe-utilities/lib/request';
import { CUSTOMER_BILL } from '@oskecare/fe-utilities/lib/constants/dataSources';
import { cacheWithErrorCheck } from '../../helpers.js';
import BillingReport from './models/BillingReport.js';

export default class Billing {
  /**
   * @param {object} config
   * @param {Logger} logger
   * @param {CustomerDataManager} customerDataManager
   */
  constructor({ config, logger, customerDataManager }) {
    this._natalieApiUrl = config.natalieApiUrl;
    this._invoiceBaseUrl = config.invoiceBaseUrl;
    this._customerBillCalculationApiUrl = config.customerBillCalculationApiUrl;
    this._logger = logger;
    this._customerDataManager = customerDataManager;
    this._cache = new Map();
  }

  /**
   * Proxy for `request` function with logSource pre-set
   *
   * @param {object} params
   * @return {Promise<*>}
   * @see Request.fetch
   * @private
   */
  request(params) {
    return request({
      ...params,
      logSource: 'infra-billing',
    }).catch(this._logger.catchError);
  }

  /**
   * @param {boolean} isNonessential
   * @return {Promise<*>}
   */
  getInvoiceList(isNonessential = false) {
    return cacheWithErrorCheck(this._cache, 'list', () =>
      this.request({
        method: 'GET',
        route: `${this._natalieApiUrl}/list`,
        dataSource: CUSTOMER_BILL,
        isNonessential,
      }),
    );
  }

  /**
   * @param {boolean} isNonessential
   * @return {Promise<*>}
   */
  getInvoiceSummary(isNonessential = false) {
    return cacheWithErrorCheck(this._cache, 'summary', () =>
      this.request({
        method: 'GET',
        route: `${this._natalieApiUrl}/summary`,
        dataSource: CUSTOMER_BILL,
        isNonessential,
      }),
    );
  }

  /**
   * @param {boolean} isNonessential
   * @return {Promise<BillingReport>}
   */
  async getBillingReport(isNonessential = false) {
    const { billingAccountId, isCustomerAdmin } = (
      await this._customerDataManager.getCustomerData()
    ).cnData;
    const { sn } = (await this._customerDataManager.getCustomerData()).loggedInSnData;
    const query = isCustomerAdmin ? { 'billingAccount.id': billingAccountId } : { publicKey: sn };

    return cacheWithErrorCheck(this._cache, 'customerBill', () =>
      this.request({
        method: 'GET',
        route: `${this._customerBillCalculationApiUrl}/customerBill`,
        query,
        dataSource: CUSTOMER_BILL,
        isNonessential,
      }).then(data => new BillingReport({ data, invoiceBaseUrl: this._invoiceBaseUrl })),
    );
  }

  resetCache() {
    this._cache.clear();
  }
}
